import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Alert, AlertDescription } from "./ui/alert";
import { toast } from "sonner";
import { UserCheck, UserPlus, Info, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal ligeiro para criar cliente rapidamente durante criação de ordem/encomenda manual.
 * - Verifica duplicado por NIF em tempo real
 * - Mostra confirmação antes de guardar
 * Props:
 *  - open, onClose
 *  - initialName (opcional — pré-preencher se veio de outro sítio)
 *  - onCreated: (client) => void — retorna o cliente criado ou existente
 */
export default function ClientQuickCreateDialog({ open, onClose, initialName = "", onCreated }) {
  const [form, setForm] = useState({
    name: "", nif: "", address: "", city: "", postal_code: "",
    phone: "", email: "", contact_person: "", notes: "",
  });
  const [existing, setExisting] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ name: initialName, nif: "", address: "", city: "", postal_code: "",
                phone: "", email: "", contact_person: "", notes: "" });
      setExisting(null);
      setConfirming(false);
    }
  }, [open, initialName]);

  // Lookup em tempo real por NIF (debounced 500ms)
  useEffect(() => {
    const nif = form.nif?.trim();
    if (!nif || nif.length < 5) { setExisting(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/clients/lookup?nif=${encodeURIComponent(nif)}`);
        setExisting(r.data.found ? r.data.matches[0] : null);
      } catch {}
    }, 500);
    return () => clearTimeout(t);
  }, [form.nif]);

  const useExisting = () => {
    onCreated?.(existing);
    toast.info(`Cliente ${existing.name} reutilizado`);
    onClose();
  };

  const doCreate = async () => {
    if (!form.name.trim()) { toast.error("Nome obrigatório"); return; }
    try {
      setSubmitting(true);
      const payload = {};
      Object.entries(form).forEach(([k, v]) => { payload[k] = (v || "").toString().trim(); });
      const r = await axios.post(`${API}/clients`, payload);
      toast.success(`Cliente "${r.data.name}" criado`);
      onCreated?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a criar cliente");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="client-quick-create-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-emerald-600" />
            {confirming ? "Confirmar dados do cliente" : "Novo cliente"}
          </DialogTitle>
          <DialogDescription>
            {confirming ? "Verifica os dados antes de criar. Este cliente ficará disponível em toda a app."
                        : "Preenche os dados. Se o NIF já existir, será oferecida a opção de reutilizar."}
          </DialogDescription>
        </DialogHeader>

        {existing && !confirming && (
          <Alert className="bg-amber-50 border-amber-300">
            <Info className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-900">
              <b>Cliente com NIF {existing.nif} já existe:</b> {existing.name}
              <div className="mt-2">
                <Button size="sm" onClick={useExisting} data-testid="quick-use-existing-btn" className="bg-amber-600 hover:bg-amber-700">
                  <UserCheck className="h-3.5 w-3.5 mr-1" /> Usar este cliente
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-2 gap-3 mt-2">
          <div className="col-span-2">
            <Label>Nome *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} disabled={confirming} data-testid="qc-name" />
          </div>
          <div>
            <Label>NIF</Label>
            <Input value={form.nif} onChange={(e) => setForm({ ...form, nif: e.target.value })} disabled={confirming} data-testid="qc-nif" />
          </div>
          <div>
            <Label>Contacto</Label>
            <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} disabled={confirming} />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={confirming} data-testid="qc-email" />
          </div>
          <div>
            <Label>Telefone</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} disabled={confirming} />
          </div>
          <div className="col-span-2">
            <Label>Morada</Label>
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} disabled={confirming} />
          </div>
          <div>
            <Label>Localidade</Label>
            <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} disabled={confirming} />
          </div>
          <div>
            <Label>Código Postal</Label>
            <Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} disabled={confirming} />
          </div>
        </div>

        <DialogFooter className="mt-3 gap-2">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          {!confirming && (
            <Button
              onClick={() => {
                if (!form.name.trim()) { toast.error("Nome obrigatório"); return; }
                setConfirming(true);
              }}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="qc-review-btn"
            >
              Rever dados →
            </Button>
          )}
          {confirming && (
            <>
              <Button variant="outline" onClick={() => setConfirming(false)}>← Editar</Button>
              <Button onClick={doCreate} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" data-testid="qc-create-btn">
                {submitting ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> A criar…</> : "Confirmar e criar cliente"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
